import type { StatefulDiaryT } from "@/types/database/diaries";
import type { StatefulTrackerViewT } from "@patient-watch/types";
import { isProcedureSideUnset } from "./procedureSide";

export type MissingSideItem =
    | { kind: "surgery"; surgeryId: string; label: string }
    | { kind: "injection"; injectionDetailId: string; label: string }
    | { kind: "shockwave"; shockwaveDetailId: string; label: string }
    | { kind: "tracker"; trackerId: string; formId: string; label: string };

export function missingSideItemKey(item: MissingSideItem): string {
    if (item.kind === "surgery") return `surgery:${item.surgeryId}`;
    if (item.kind === "injection") return `injection:${item.injectionDetailId}`;
    if (item.kind === "shockwave") return `shockwave:${item.shockwaveDetailId}`;
    return `tracker:${item.trackerId}`;
}

function isTrackerSideUnset(tracker: StatefulTrackerViewT): boolean {
    if (!tracker.form?.is_side_specific) return false;
    if (tracker.side === "Both") return true;
    return isProcedureSideUnset(tracker.side as string | null | undefined);
}

export function buildMissingSideItems(diary: StatefulDiaryT): MissingSideItem[] {
    const items: MissingSideItem[] = [];

    for (const s of diary.events_view?.flatMap((e) => e?.surgeries_view ?? []) ?? []) {
        if (s?.surgery_id && isProcedureSideUnset(s.side)) {
            items.push({ kind: "surgery", surgeryId: s.surgery_id, label: "your surgery (procedure)" });
        }
    }

    for (const ev of diary.events_view ?? []) {
        for (const inj of ev?.injections_view ?? []) {
            for (const d of inj?.injection_details ?? []) {
                if (d?.injection_detail_id && isProcedureSideUnset(d.side)) {
                    items.push({
                        kind: "injection",
                        injectionDetailId: d.injection_detail_id,
                        label: "your injection (procedure)",
                    });
                }
            }
        }
    }

    for (const ev of diary.events_view ?? []) {
        for (const sw of ev?.shockwaves_view ?? []) {
            for (const d of sw?.shockwave_details ?? []) {
                if (d?.shockwave_detail_id && isProcedureSideUnset(d.side)) {
                    items.push({
                        kind: "shockwave",
                        shockwaveDetailId: d.shockwave_detail_id,
                        label: "your shockwave treatment (procedure)",
                    });
                }
            }
        }
    }

    for (const t of diary.trackers_view ?? []) {
        const tr = t as StatefulTrackerViewT;
        if (tr?.tracker_id && isTrackerSideUnset(tr)) {
            const name = tr.form?.form_name ?? "Questionnaire";
            items.push({
                kind: "tracker",
                trackerId: tr.tracker_id,
                formId: tr.form_id ?? "",
                label: name,
            });
        }
    }

    return items;
}
